import React, { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Grid, ExternalLink, Lightbulb, ChevronDown } from 'lucide-react'
import StockHeatmap from '../StockHeatmap'
import EtfHeatmap from '../EtfHeatmap'
import FinvizBubbleMap from '../FinvizBubbleMap'
import { pageContainerVariants, pageItemVariants } from '../../constants/motionVariants'

const BUBBLE_TIPS = [
  { icon: '🎨', text: 'Sectors แบ่งตามสี — เอาเมาส์จิ้มดูรายตัวได้' },
  { icon: '📈', text: 'แกนตั้ง คือ การปรับตัวของราคา %Change' },
  { icon: '⬆️', text: 'วงกลมอยู่เหนือระดับ 0 = ปรับตัวอยู่โซนบวก' },
  { icon: '🔵', text: 'ขนาดวงกลม แทนมูลค่าตลาด Market Cap. ของหุ้นนั้นๆ' },
  { icon: '🏆', text: 'หาหุ้นใหญ่บวกเยอะ — เล็งวงกลมใหญ่ที่อยู่สูง' },
  { icon: '🚀', text: 'หาหุ้นกลาง-เล็กบวกเยอะ — เล็งวงกลมเล็กที่อยู่สูง' },
  { icon: '🔥', text: 'หา Sector โมเมนตัมดี — หากลุ่มสีที่อยู่เหนือระดับ 0' },
  { icon: '⚠️', text: 'กลุ่มสีที่อยู่ใต้ระดับ 0 — ให้ระวัง Sectors กลุ่มนั้น' },
  { icon: '📊', text: 'เลือกดูได้ทุกดัชนี S&P500 / Nasdaq / Dow Jones / Russell' },
  { icon: '⭐', text: 'หาหุ้นโดดเด่นเหนือเพื่อนกลุ่มเดียวกัน — เล็งตัวที่กระโดดฉีก' },
  { icon: '🖱️', text: 'เจอหุ้นที่ใช่ — ดับเบิ้ลคลิกที่วงกลมนั้น จะปรากฏกราฟ' },
  { icon: '⚡', text: 'เพียงเท่านี้ก็ประหยัดแรง ประหยัดเวลา สกรีนตลาดไปได้เพียบ!' },
]

const HeatmapPage: React.FC = () => {
  const [tipsOpen, setTipsOpen] = useState(false)

  return (
    <motion.div variants={pageContainerVariants} initial="hidden" animate="visible" className="space-y-6">

      {/* Finviz Bubble Map — top */}
      <motion.div variants={pageItemVariants} className="card p-6 ">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span>🫧</span>
            Finviz Bubble Map (S&P 500)
          </h2>
          <a
            href="https://finviz.com/bubbles?idx=sp500"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            <ExternalLink className="w-3 h-3" />
            เปิดในหน้าต่างใหม่
          </a>
        </div>
        <div
          className="bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{ height: 'calc(60vh - 100px)', minHeight: '480px' }}
        >
          <FinvizBubbleMap />
        </div>
      </motion.div>

      {/* Tips accordion */}
      <motion.div variants={pageItemVariants} className="bg-amber-50/80 dark:bg-amber-900/20 backdrop-blur-sm rounded-2xl border border-amber-200/60 dark:border-amber-700/40 overflow-hidden">
        <button
          onClick={() => setTipsOpen((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-3.5 text-left"
        >
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500 dark:text-amber-400" />
            <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              วิธีอ่าน Finviz Bubble Map
            </span>
            <a
              href="https://finviz.com/bubbles?idx=sp500"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/40 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              เปิด Finviz
            </a>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-amber-500 dark:text-amber-400 transition-transform duration-200 ${tipsOpen ? 'rotate-180' : ''}`}
          />
        </button>

        <AnimatePresence initial={false}>
          {tipsOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {BUBBLE_TIPS.map(({ icon, text }, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 bg-white/60 dark:bg-gray-800/40 rounded-xl px-3 py-2.5"
                  >
                    <span className="text-base leading-none mt-0.5">{icon}</span>
                    <span className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Stock Heatmap */}
      <motion.div variants={pageItemVariants} className="card p-6 ">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Grid className="w-5 h-5" />
            Stock Heatmap (S&P 500)
          </h2>
        </div>
        <div
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{ height: 'calc(50vh - 150px)', minHeight: '400px' }}
        >
          <StockHeatmap />
        </div>
      </motion.div>

      {/* ETF Heatmap */}
      <motion.div variants={pageItemVariants} className="card p-6 ">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Grid className="w-5 h-5" />
            ETF Heatmap (US ETFs)
          </h2>
        </div>
        <div
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{ height: 'calc(50vh - 150px)', minHeight: '400px' }}
        >
          <EtfHeatmap />
        </div>
      </motion.div>

    </motion.div>
  )
}

export default HeatmapPage
